import { useCallback } from "react";

import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";

interface VatFreeCompanyReturn {
	/**
	 * Get if a company is vat free for the given dates.
	 * This should be used when bookkeeping bank transactions when you have a date
	 * to work with.
	 * @param dates the dates you want to get VAT settings for
	 * @returns if the VAT setting is VAT free for all dates
	 */
	isVatFreeForAllDates: (dates: m.Day[], fallbackToCurrentVatSetting?: boolean) => boolean;
	/**
	 * Get if a company is currently set to vat free.
	 * This should be used when bookkeeping receipts/invoices where you don't have a date
	 * to work with when starting the bookkeeping journey. You currently start with searching
	 * bookkeeping templates and then we don't have a date to work with.
	 * @returns if the VAT setting is VAT free for the current date
	 */
	currentSettingIsVatFree: () => boolean;
}

export const useVatFreeCompany = (): VatFreeCompanyReturn => {
	const { companyInfo } = useCompanyInfo();

	const vatSettingsForDates = useCallback(
		(dates: m.Day[]) => {
			const allSettings = companyInfo.CompanyVatSettings;

			const relevantSettings = allSettings.filter(setting =>
				dates.some(date => {
					const settingEnd = setting.EndDate ?? m.Day.fromDate(new Date(9999, 11, 31));
					return date >= setting.StartDate && date <= settingEnd;
				}),
			);

			const distinctSettings = Array.from(new Map(relevantSettings.map(s => [s.VatSetting, s])).values());

			return distinctSettings;
		},
		[companyInfo.CompanyVatSettings],
	);

	/** Check if VAT settings for the given dates are VAT free
	 *  If one date has a non VAT free setting, the result is false
	 */
	const isVatFreeForAllDates = useCallback(
		(dates: m.Day[], fallbackToCurrentVatSetting = true) => {
			if (dates.length === 0) {
				return fallbackToCurrentVatSetting
					? companyInfo.ActiveVatSetting === m.Entities.VatSetting.NotRegisteredForVat
					: false;
			}
			const vatSettings = vatSettingsForDates(dates);
			if (!vatSettings.length) {
				throw new Error("No VAT settings found for the given dates");
			}
			return vatSettings.every(setting => setting.VatSetting === m.Entities.VatSetting.NotRegisteredForVat);
		},
		[vatSettingsForDates, companyInfo.ActiveVatSetting],
	);

	const currentSettingIsVatFree = useCallback(
		() => companyInfo.ActiveVatSetting === m.Entities.VatSetting.NotRegisteredForVat,
		[companyInfo.ActiveVatSetting],
	);

	return { isVatFreeForAllDates, currentSettingIsVatFree };
};
